/**
 * AUTO-GENERATED from the "Role permissions" Google Sheet, DO NOT EDIT.
 * See "Adding or changing a permission" in the README.
 */
import type { IdentityRole } from "../models/index.js";
import type { PermissionPath } from "./permissionTypes.js";

export const rolePermissions: Record<
  IdentityRole,
  { permissions: PermissionPath[] }
> = {
  owner: {
    permissions: [
      "account.read.billing",
      "account.update.billing",
      "asset.delete",
      "asset.read",
      "asset.update",
      "connection.delete",
      "connection.read",
      "connection.update",
      "coupon.read",
      "coupon.update",
      "fact.delete",
      "fact.read",
      "fact.update",
      "fact.update.approval",
      "fact.update.blocks-permissions",
      "fact.update.stacks",
      "feature-setting.read",
      "feature-setting.update",
      "graph.read",
      "identity.create",
      "identity.delete",
      "identity.update.profile-advanced",
      "identity.update.profile-basic",
      "insight.read",
      "insight.update",
      "network.delete",
      "network.read",
      "network.update",
      "note.read",
      "note.update",
      "organization.create",
      "organization.read",
      "organization.update",
      "organization.update.ownership",
      "referral.read",
      "task.read",
      "task.update",
      "workspace.read",
    ],
  },
  executive: {
    permissions: [
      "asset.delete",
      "asset.read",
      "asset.update",
      "connection.delete",
      "connection.read",
      "connection.update",
      "coupon.read",
      "coupon.update",
      "fact.delete",
      "fact.read",
      "fact.update",
      "fact.update.approval",
      "fact.update.blocks-permissions",
      "fact.update.stacks",
      "feature-setting.read",
      "feature-setting.update",
      "graph.read",
      "identity.update.profile-advanced",
      "identity.update.profile-basic",
      "insight.read",
      "insight.update",
      "network.delete",
      "network.read",
      "network.update",
      "note.read",
      "note.update",
      "organization.read",
      "organization.update",
      "referral.read",
      "task.read",
      "task.update",
      "workspace.read",
    ],
  },
  marketing: {
    permissions: [
      "asset.delete",
      "asset.read",
      "asset.update",
      "connection.read",
      "coupon.read",
      "coupon.update",
      "fact.read",
      "fact.update",
      "feature-setting.read",
      "identity.update.profile-basic",
      "network.read",
      "network.update",
      "note.read",
      "note.update",
      "referral.read",
      "task.read",
      "task.update",
      "workspace.read",
    ],
  },
  sales: {
    permissions: [
      "asset.delete",
      "asset.read",
      "asset.update",
      "connection.read",
      "coupon.read",
      "coupon.update",
      "fact.read",
      "feature-setting.read",
      "identity.update.profile-basic",
      "network.read",
      "network.update",
      "referral.read",
      "task.read",
      "task.update",
      "workspace.read",
    ],
  },
  creator: {
    permissions: [
      "asset.delete",
      "asset.read",
      "asset.update",
      "fact.read",
      "feature-setting.read",
      "identity.update.profile-basic",
      "note.read",
      "note.update",
      "referral.read",
      "task.read",
      "task.update",
      "workspace.read",
    ],
  },
  advisor: {
    permissions: [
      "asset.delete",
      "asset.read",
      "asset.update",
      "connection.read",
      "fact.read",
      "fact.update",
      "feature-setting.read",
      "task.read",
      "task.update",
    ],
  },
  guest: {
    permissions: ["fact.read", "fact.update", "feature-setting.read"],
  },
  bot: {
    permissions: [
      "asset.read",
      "asset.update",
      "fact.read",
      "fact.update",
      "feature-setting.read",
      "insight.read",
      "network.read",
      "network.update",
      "note.read",
      "note.update",
      "task.read",
      "task.update",
    ],
  },
};

export const allPermissions: Array<{
  id: PermissionPath;
  action: string;
  category: string;
  learnMoreUrl?: string;
  learnMoreLabel?: string;
  showInUI: boolean;
}> = [
  {
    id: "account.update.billing",
    action: "Can manage payment and billing information",
    category: "Account Management",
    showInUI: true,
  },
  {
    id: "account.read.billing",
    action: "Can view billing information",
    category: "Account Management",
    showInUI: true,
  },
  {
    id: "asset.read",
    action: "Can access assets",
    category: "Assets",
    showInUI: true,
  },
  {
    id: "asset.update",
    action: "Can create and edit created assets",
    category: "Assets",
    showInUI: true,
  },
  {
    id: "asset.delete",
    action: "Can delete user-owned assets",
    category: "Assets",
    showInUI: false,
  },
  {
    id: "coupon.update",
    action: "Can apply discount codes",
    category: "Benefits",
    showInUI: false,
  },
  {
    id: "coupon.read",
    action: "Can view available discount codes and promotions",
    category: "Benefits",
    showInUI: false,
  },
  {
    id: "fact.read",
    action: "Can access facts",
    category: "Facts",
    showInUI: true,
  },
  {
    id: "fact.update.approval",
    action: "Can approve or reject fact changes",
    category: "Facts",
    showInUI: true,
  },
  {
    id: "fact.update.stacks",
    action: "Can create, edit and delete stacks and blocks",
    category: "Facts",
    showInUI: true,
  },
  {
    id: "fact.delete",
    action: "Can delete all facts",
    category: "Facts",
    showInUI: true,
  },
  {
    id: "fact.update.blocks-permissions",
    action: "Can manage permissions for facts",
    category: "Facts",
    showInUI: true,
  },
  {
    id: "fact.update",
    action: "Can update facts",
    category: "Facts",
    showInUI: true,
  },
  {
    id: "feature-setting.read",
    action: "Can access feature settings",
    category: "Feature Settings",
    showInUI: false,
  },
  {
    id: "feature-setting.update",
    action: "Can configure Advisor Mode",
    category: "Feature Settings",
    showInUI: true,
  },
  {
    id: "graph.read",
    action: "Can view the company graph",
    category: "Graph",
    showInUI: true,
  },
  {
    id: "insight.update",
    action: "Can dismiss and reopen insights on facts",
    category: "Insights",
    showInUI: true,
  },
  {
    id: "insight.read",
    action:
      "Can view insights on facts: duplicates, unlinked facts and coverage gaps",
    category: "Insights",
    showInUI: true,
  },
  {
    id: "connection.update",
    action: "Can connect external apps and create API keys",
    category: "Integrations",
    showInUI: true,
  },
  {
    id: "connection.delete",
    action: "Can delete connected apps and revoke API keys",
    category: "Integrations",
    showInUI: false,
  },
  {
    id: "connection.read",
    action: "Can view connected apps",
    category: "Integrations",
    showInUI: true,
  },
  {
    id: "network.read",
    action: "Can access the project's contact network",
    category: "Network",
    showInUI: true,
  },
  {
    id: "network.delete",
    action: "Can delete any contact",
    category: "Network",
    showInUI: true,
  },
  {
    id: "network.update",
    action: "Can manage contacts",
    category: "Network",
    showInUI: true,
  },
  {
    id: "note.read",
    action: "Can access the project's notes",
    category: "Notes",
    showInUI: true,
  },
  {
    id: "note.update",
    action: "Can manage notes",
    category: "Notes",
    showInUI: true,
  },
  {
    id: "organization.create",
    action: "Can create new projects",
    category: "Project Settings",
    showInUI: true,
  },
  {
    id: "organization.update",
    action: "Can manage project settings and details",
    category: "Project Settings",
    showInUI: true,
  },
  {
    id: "organization.update.ownership",
    action: "Can transfer project ownership",
    category: "Project Settings",
    showInUI: true,
  },
  {
    id: "organization.read",
    action: "Can view project details and information",
    category: "Project Settings",
    showInUI: true,
  },
  {
    id: "referral.read",
    action: "Can view referral program status and rewards",
    category: "Referrals",
    showInUI: false,
  },
  {
    id: "task.update",
    action: "Can manage tasks",
    category: "Tasks",
    showInUI: true,
  },
  {
    id: "task.read",
    action: "Can view tasks",
    category: "Tasks",
    showInUI: true,
  },
  {
    id: "identity.create",
    action: "Can invite users to the project",
    category: "User Management",
    showInUI: true,
  },
  {
    id: "identity.update.profile-advanced",
    action: "Can manage user access and roles",
    category: "User Management",
    showInUI: true,
  },
  {
    id: "identity.delete",
    action: "Can revoke user access from the project",
    category: "User Management",
    showInUI: true,
  },
  {
    id: "identity.update.profile-basic",
    action: "Can update profile information",
    category: "User Management",
    showInUI: true,
  },
  {
    id: "workspace.read",
    action: "Can access NOAN Assistant",
    category: "Workspace",
    showInUI: true,
  },
];
